/* ───────────────────────────────────────────────────────────────────────────
 * lcsClient.js — staged-overlay HTTP client for the LCS decision cockpit.
 *
 * Auth/context model (mirrors ForecastDashboard / Charts.js):
 *   The parent page is loaded with tenant/auth/lease params on the URL —
 *   e.g. demo.ogpumper.net/?Company=lcs&UserID=admin&token=…&grant=… — and
 *   THIS client reads those params via getOgPumperUrlContext() and forwards
 *   them on every overlay_staged request. No PHP proxy, no bundled secrets.
 *
 * Default destination is the canonical staged endpoint:
 *   https://lcs.ogpumper.net/api/overlay_staged.php
 *
 * Override via REACT_APP_API_BASE if you want to hit a different host
 * (e.g. local proxy at http://localhost:8000/api during dev). The same
 * ForecastDashboard URL params still drive auth.
 * ──────────────────────────────────────────────────────────────────────── */

import { getOgPumperUrlContext, applyContextToUrl } from "./ogpUrlContext";

// Where the overlay_staged endpoint lives. Default to the LCS host so the
// production build calls it directly with URL-supplied auth (the existing
// OgPumper pattern). Local dev can override.
function _resolveBase() {
  const fromForecast = (typeof process !== "undefined" && process?.env?.REACT_APP_FORECAST_API_BASE) || "";
  const fromApi = (typeof process !== "undefined" && process?.env?.REACT_APP_API_BASE) || "";
  // Strip trailing slashes; ensure one /api segment.
  function norm(b) {
    let s = String(b).replace(/\/+$/, "");
    if (!/\/api(\/|$)/.test(s)) s += "/api";
    return s;
  }
  if (fromForecast) return norm(fromForecast);
  if (fromApi) return norm(fromApi);
  return "https://lcs.ogpumper.net/api";
}
export const API_BASE = _resolveBase();

const STAGED_PATH = "/overlay_staged.php";

let _warned = false;
function _warnIfNoCreds(ctx) {
  if (_warned || typeof console === "undefined") return;
  if (!ctx.hasAuth) {
    _warned = true;
    console.warn(
      "[lcsClient] No token/grant on URL or in env — overlay_staged calls will 401. " +
      "Open the cockpit with ?token=…&grant=… (same shape as ForecastDashboard).",
    );
  }
}

/**
 * Fetch a staged overlay for one LCS lease.
 *
 * @param {object} args
 * @param {string} args.leaseId          - required
 * @param {number} [args.horizon=60]     - URL ?horizon= overrides
 * @param {string[]|string} [args.streams]
 * @param {boolean} [args.zeroAsNull=true]
 * @param {boolean} [args.showMA=true]
 * @param {number}  [args.maWindow=7]
 * @param {AbortSignal} [args.signal]
 * @param {string}  [args.apiBase]       - override base for this call
 */
export async function fetchLcsLeaseOverlay(args) {
  const {
    leaseId,
    horizon,
    streams,
    zeroAsNull = true,
    showMA = true,
    maWindow = 7,
    signal,
    apiBase,
  } = args || {};

  if (!leaseId || typeof leaseId !== "string") {
    throw new Error("fetchLcsLeaseOverlay: leaseId is required");
  }

  const ctx = getOgPumperUrlContext();
  _warnIfNoCreds(ctx);

  const base = (apiBase || API_BASE).replace(/\/+$/, "");
  const url = new URL(base + STAGED_PATH, typeof window !== "undefined" ? window.location.origin : "http://localhost");
  url.searchParams.set("lease", leaseId);
  url.searchParams.set("horizon", String(horizon ?? ctx.horizon ?? 60));
  const streamsStr = Array.isArray(streams) ? streams.join(",") : (streams || ctx.streams || "Oil,Gas,Water");
  url.searchParams.set("streams", streamsStr);
  url.searchParams.set("limit", "250");
  if (zeroAsNull) url.searchParams.set("zeroAsNull", "1");
  if (showMA) url.searchParams.set("showMA", "1");
  if (Number.isFinite(maWindow)) url.searchParams.set("maWindow", String(maWindow));

  // Inject every OgPumper auth/context param from the page URL.
  applyContextToUrl(url, ctx);

  const res = await fetch(url.toString(), {
    signal,
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    let detail = "";
    try {
      const ct = res.headers.get("content-type") || "";
      detail = ct.includes("application/json")
        ? JSON.stringify(await res.json())
        : await res.text();
    } catch { /* ignore */ }
    throw new Error(`LCS overlay ${res.status}: ${detail || res.statusText}`);
  }
  return res.json();
}

/** List staged leases (?list_leases=1). Same auth/context rules. */
export async function fetchLcsLeasesWithForecasts({ limit = 250, apiBase, signal } = {}) {
  const ctx = getOgPumperUrlContext();
  _warnIfNoCreds(ctx);

  const base = (apiBase || API_BASE).replace(/\/+$/, "");
  const url = new URL(base + STAGED_PATH, typeof window !== "undefined" ? window.location.origin : "http://localhost");
  url.searchParams.set("list_leases", "1");
  url.searchParams.set("limit", String(limit));
  applyContextToUrl(url, ctx);

  const res = await fetch(url.toString(), { signal, headers: { Accept: "application/json" } });
  if (!res.ok) throw new Error(`list_leases ${res.status}`);
  return res.json();
}

/** Diagnostic: surface the resolved auth context for the cockpit telemetry strip. */
export function lcsClientInfo() {
  const ctx = getOgPumperUrlContext();
  return {
    base: API_BASE,
    path: STAGED_PATH,
    hasAuth: ctx.hasAuth,
    company: ctx.company,
    userId: ctx.userId,
  };
}

export default fetchLcsLeaseOverlay;
